import React, { useMemo } from 'react';
import { MContentHeader } from '../../molecules/MContentHeader/index';
import {
  MContentHeaderAlignment,
  type MContentHeaderAllowedButtonType,
  type MContentHeaderButton,
} from '../../molecules/MContentHeader/MContentHeader.types';
import { MButtonType } from '../../molecules/MButton/MButton.types';
import { MSlot } from '../../molecules/MSlot/index';
import type { OMiniCardContainerCqItem, OMiniCardContainerProps } from './OMiniCardContainer.types';
import * as Styled from './OMiniCardContainer.styles';

const COMPONENT_NAME = 'o-web-ui-kit-OMiniCardContainer';
const TEST_ID = 'web-ui-kit-OMiniCardContainer';

const EMPTY_AEM_REACT_COMPONENT_MAPPING = {};
const MAX_VISIBLE_CARDS = 9;
const SUPPORTED_DESKTOP_COLUMNS = [2, 3] as const;
const DEFAULT_DESKTOP_COLUMNS = 2;
const DEFAULT_NO_OF_TOTAL_CARDS = 3;

const ALLOWED_HEADER_BUTTON_TYPES = new Set<string>([MButtonType.Secondary, MButtonType.Tertiary]);

const parseDesktopColumns = (value?: string): number => {
  const trimmed = value?.trim();
  const parsed = trimmed ? parseInt(trimmed, 10) : NaN;
  return !Number.isNaN(parsed) &&
    SUPPORTED_DESKTOP_COLUMNS.includes(parsed as (typeof SUPPORTED_DESKTOP_COLUMNS)[number])
    ? parsed
    : DEFAULT_DESKTOP_COLUMNS;
};

/**
 * Primary is not allowed on this organism. Secondary (and Tertiary) default to size L
 * when AEM does not send a size. Authored S / M / L are kept.
 */
const applyMiniCardHeaderButtonDefaults = (button?: MContentHeaderButton | null): MContentHeaderButton | undefined => {
  if (!button) {
    return undefined;
  }

  const requested = button.buttonType ?? button.type;
  const buttonType: MContentHeaderAllowedButtonType = ALLOWED_HEADER_BUTTON_TYPES.has(requested ?? '')
    ? (requested as MContentHeaderAllowedButtonType)
    : MButtonType.Secondary;

  return {
    ...button,
    buttonType,
    size: button.size || 'L',
  };
};

const toCtaFlag = (value: unknown): 'true' | 'false' | undefined => {
  if (value === true || value === 'true') {
    return 'true';
  }
  if (value === false || value === 'false') {
    return 'false';
  }
  return undefined;
};

const getOrderedItemKeys = (items: Record<string, unknown>, order?: string[]): string[] => {
  if (order?.length) {
    return order.filter(itemKey => itemKey in items);
  }
  return Object.keys(items);
};

const parseTotalNoOfCards = (value: number | string | undefined): number => {
  const trimmed = typeof value === 'string' ? value.trim() : undefined;
  const parsed = typeof value === 'number' ? value : trimmed ? parseInt(trimmed, 10) : NaN;
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 0;
  }
  return Math.min(MAX_VISIBLE_CARDS, Math.floor(parsed));
};

const getSlotCqItems = (
  slotModel: OMiniCardContainerCqItem | undefined,
  slotKey: string
): Record<string, OMiniCardContainerCqItem> => {
  if (!slotModel) {
    return {};
  }

  const nested = (slotModel[':items'] ?? slotModel.cqItems) as Record<string, OMiniCardContainerCqItem> | undefined;
  if (nested && Object.keys(nested).length > 0) {
    return nested;
  }

  if (slotModel[':type'] || slotModel.cqType) {
    return { [slotKey]: slotModel };
  }

  return {};
};

const getSlotOrder = (
  slotModel: OMiniCardContainerCqItem | undefined,
  slotItems: Record<string, unknown>
): string[] => {
  const nestedOrder = (slotModel?.[':itemsOrder'] ?? slotModel?.cqItemsOrder) as string[] | undefined;
  if (nestedOrder?.length) {
    return nestedOrder;
  }
  return Object.keys(slotItems);
};

export const OMiniCardContainer: React.FC<OMiniCardContainerProps> = ({
  desktopColumns,
  totalNoOfCards,
  mContentHeader,
  pagePath = '',
  itemPath = '',
  cqPath,
  isAuthorMode = false,
  aemReactComponentMapping = EMPTY_AEM_REACT_COMPONENT_MAPPING,
  className = '',
  children,
  model,
  ':items': aemItems,
  ':itemsOrder': aemItemsOrder,
  cqItems,
  cqItemsOrder,
}) => {
  const resolvedDesktopColumns =
    desktopColumns ?? (model?.['desktopColumns'] as string | undefined) ?? String(DEFAULT_DESKTOP_COLUMNS);
  const desktopColumnsNum = parseDesktopColumns(resolvedDesktopColumns);
  const resolvedNoOfTotalCards =
    totalNoOfCards ??
    (model?.['totalNoOfCards'] as number | string | undefined) ??
    (isAuthorMode ? DEFAULT_NO_OF_TOTAL_CARDS : undefined);
  const totalCardSlots = parseTotalNoOfCards(resolvedNoOfTotalCards);
  const resolvedMContentHeader = (mContentHeader ??
    model?.['mContentHeader']) as OMiniCardContainerProps['mContentHeader'];
  const resolvedPagePath = pagePath || (model?.['pagePath'] as string | undefined) || '';
  const resolvedItemPath = itemPath || (model?.['itemPath'] as string | undefined) || cqPath || '';

  const authoredItems = (aemItems ?? cqItems ?? model?.[':items'] ?? model?.cqItems ?? {}) as Record<
    string,
    OMiniCardContainerCqItem
  >;
  const authoredOrder =
    aemItemsOrder ?? cqItemsOrder ?? model?.[':itemsOrder'] ?? model?.cqItemsOrder ?? Object.keys(authoredItems);
  const authoredItemKeys = useMemo(
    () => getOrderedItemKeys(authoredItems, authoredOrder).slice(0, MAX_VISIBLE_CARDS),
    [authoredItems, authoredOrder]
  );
  const renderedSlotKeys = useMemo(
    () =>
      totalCardSlots > 0 ? Array.from({ length: totalCardSlots }, (_, index) => `item${index}`) : authoredItemKeys,
    [authoredItemKeys, totalCardSlots]
  );

  const childCards = useMemo(() => {
    const all = children ? React.Children.toArray(children) : [];
    return all.slice(0, MAX_VISIBLE_CARDS);
  }, [children]);
  const hasChildren = childCards.length > 0;
  const hasAuthoredCards = authoredItemKeys.length > 0;
  const hasFixedSlots = renderedSlotKeys.length > 0;
  const hasHeaderText = Boolean(resolvedMContentHeader?.mTextBlockSimple?.aHeader?.text);

  const headerSwap = resolvedMContentHeader?.mContentSwap;
  const headerButton = applyMiniCardHeaderButtonDefaults(
    (headerSwap?.mButton as MContentHeaderButton | undefined) ?? resolvedMContentHeader?.mButton
  );
  const headerButton2 = applyMiniCardHeaderButtonDefaults(
    (headerSwap?.mButton2 as MContentHeaderButton | undefined) ?? resolvedMContentHeader?.mButton2
  );
  const headerCta = toCtaFlag(resolvedMContentHeader?.cta) ?? (headerButton ? 'true' : undefined);
  const headerSecondaryCta =
    toCtaFlag(resolvedMContentHeader?.secondaryCta) ??
    toCtaFlag(headerSwap?.button2) ??
    (headerButton2 ? 'true' : undefined);

  if (!hasChildren && !hasAuthoredCards && !hasFixedSlots && !isAuthorMode) {
    return null;
  }

  return (
    <Styled.Wrapper className={className} data-component-name={COMPONENT_NAME} data-testid={TEST_ID}>
      <Styled.GridContainer className="container px-0">
        {hasHeaderText && resolvedMContentHeader && (
          <Styled.HeaderWrapper>
            <MContentHeader
              alignment={resolvedMContentHeader.alignment || MContentHeaderAlignment.Left}
              mTextBlockSimple={resolvedMContentHeader.mTextBlockSimple}
              cta={headerCta}
              mButton={headerButton}
              secondaryCta={headerSecondaryCta}
              mButton2={headerButton2}
            />
          </Styled.HeaderWrapper>
        )}

        <Styled.CardsGrid
          $desktopColumns={desktopColumnsNum}
          $isAuthorMode={isAuthorMode}
          data-desktop-columns={desktopColumnsNum}
        >
          {hasChildren
            ? childCards
            : renderedSlotKeys.map(slotKey => {
                const slotModel = authoredItems[slotKey];
                const slotItems = getSlotCqItems(slotModel, slotKey);
                const slotOrder = getSlotOrder(slotModel, slotItems);

                return (
                  <MSlot
                    key={slotKey}
                    pagePath={resolvedPagePath}
                    itemPath={resolvedItemPath ? `${resolvedItemPath}/${slotKey}` : slotKey}
                    cqItems={slotItems}
                    model={slotModel ? { ...slotModel, ':order': slotOrder } : { ':order': slotOrder }}
                    isAuthorMode={isAuthorMode}
                    aemReactComponentMapping={aemReactComponentMapping}
                  />
                );
              })}
        </Styled.CardsGrid>
      </Styled.GridContainer>
    </Styled.Wrapper>
  );
};
